import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Clock3,
  Gamepad2,
  Library,
  ListChecks,
  MessageCircle,
  Star,
  Trophy,
  UsersRound,
} from "lucide-react";

import "../styles/landing-editorial.css";
import ContextualAuthLink from "./ContextualAuthLink";

const productMoments = ["Biblioteca", "Sesiones", "Actividad", "Reviews", "Listas", "XP"];

export default function LandingPage() {
  return (
    <main className="landing-story">
      <section className="landing-story__hero" aria-labelledby="landing-title">
        <div className="landing-story__hero-glow" aria-hidden="true" />
        <div className="landing-story__hero-copy">
          <span className="landing-story__eyebrow">Red social de videojuegos · Beta</span>
          <h1 id="landing-title">Tu historia gamer. <em>En un solo lugar.</em></h1>
          <p className="landing-story__hero-lead">
            Steam guarda tus horas. XPHub construye tu historia gamer.
          </p>
          <p className="landing-story__hero-text">
            Reúne lo que juegas, las sesiones que recuerdas y las opiniones que te definen
            en un perfil que puedes construir y compartir.
          </p>
          <div className="landing-story__actions">
            <ContextualAuthLink className="landing-story__button landing-story__button--primary" to="/login?mode=register">
              Crear cuenta gratis <ArrowRight size={19} aria-hidden="true" />
            </ContextualAuthLink>
            <Link className="landing-story__button landing-story__button--secondary" to="/explore">
              Explorar XPHub
            </Link>
          </div>
        </div>

        <div className="landing-story__hero-product" aria-label="Vista real de XPHub">
          <ProductWindow
            src="/images/landing-product/catalog.jpg"
            alt="Catálogo público real de XPHub"
            label="XPHub por dentro"
            eager
          />
          <div className="landing-story__identity-card">
            <span className="landing-story__demo-label">Vista de ejemplo</span>
            <div className="landing-story__identity-head">
              <div className="landing-story__demo-avatar" aria-hidden="true"><Gamepad2 size={23} /></div>
              <div><strong>Tu perfil gamer</strong><span>Una identidad construida con lo que juegas</span></div>
            </div>
            <div className="landing-story__identity-stats">
              <span><strong>Nivel 78</strong>Progreso</span>
              <span><strong>87 juegos</strong>Biblioteca</span>
              <span><strong>1.339 h</strong>Historia</span>
            </div>
          </div>
        </div>
      </section>

      <div className="landing-story__product-rail" aria-label="Lo que puedes construir en XPHub">
        {productMoments.map((moment) => <span key={moment}>{moment}</span>)}
      </div>

      <ProductSection
        number="01"
        Icon={Library}
        title="Todo lo que has jugado dice algo sobre ti."
        text="Tu biblioteca, tus horas, tus favoritos y tus sesiones dejan de estar repartidos. XPHub los convierte en una identidad gamer que sigue creciendo contigo."
        chips={["Jugado", "Jugando", "Pendiente", "Favoritos"]}
      >
        <ProductWindow
          src="/images/landing-product/catalog.jpg"
          alt="Catálogo público real de XPHub"
          label="Juegos dentro de XPHub"
        />
      </ProductSection>

      <ProductSection
        number="02"
        Icon={Gamepad2}
        title="Tu historia ya existe. Impórtala."
        text="Conecta Steam y trae los juegos y las horas que ya forman parte de tu vida. Empiezas con una historia propia, no con un perfil vacío."
        reverse
      >
        <SteamPreview />
      </ProductSection>

      <ProductSection
        number="03"
        Icon={Clock3}
        title="Cada sesión cuenta."
        text="Registra cuánto has jugado, deja una nota rápida y convierte una partida cualquiera en parte de tu perfil, tu progreso y la actividad de XPHub."
        chips={["Horas", "Notas", "XP", "Actividad"]}
      >
        <SessionPreview />
      </ProductSection>

      <ProductSection
        number="04"
        Icon={UsersRound}
        title="Mira qué está jugando tu círculo."
        text="Las sesiones, reviews y listas de otras personas forman un feed vivo. Descubres juegos a través de jugadores, no de un catálogo sin rostro."
        reverse
      >
        <ProductWindow
          src="/images/landing-product/session.jpg"
          alt="Sesión pública real dentro de XPHub"
          label="Actividad en XPHub"
        />
      </ProductSection>

      <ProductSection
        number="05"
        Icon={Star}
        title="No solo juegues. Deja constancia."
        text="Valora lo que juegas, escribe lo que te hizo sentir y crea colecciones que también pasan a formar parte de tu identidad."
        chips={["Ratings", "Reviews", "Listas"]}
      >
        <ProductWindow
          src="/images/landing-product/review.jpg"
          alt="Review pública real dentro de XPHub"
          label="Opiniones de la comunidad"
        />
      </ProductSection>

      <ProductSection
        number="06"
        Icon={Trophy}
        title="Tu historia también evoluciona."
        text="Cada sesión y cada aportación construyen progreso visible. El XP no sustituye tu historia: hace tangible todo lo que has vivido jugando."
        reverse
      >
        <ProgressPreview />
      </ProductSection>

      <section className="landing-story__final" aria-labelledby="landing-final-title">
        <span className="landing-story__eyebrow">Tu próxima partida también cuenta</span>
        <h2 id="landing-final-title">Empieza a construir tu historia gamer.</h2>
        <p>Crea tu perfil, conecta Steam si quieres y deja que XPHub crezca contigo.</p>
        <div className="landing-story__actions landing-story__actions--center">
          <ContextualAuthLink className="landing-story__button landing-story__button--primary" to="/login?mode=register">
            Crear cuenta gratis <ArrowRight size={19} aria-hidden="true" />
          </ContextualAuthLink>
          <Link className="landing-story__button landing-story__button--secondary" to="/explore">
            Explorar XPHub
          </Link>
        </div>
      </section>
    </main>
  );
}

function ProductSection({ number, Icon, title, text, chips = [], reverse = false, children }) {
  return (
    <section className={`landing-story__section${reverse ? " landing-story__section--reverse" : ""}`}>
      <div className="landing-story__section-copy">
        <div className="landing-story__section-kicker"><span>{number}</span><Icon size={20} aria-hidden="true" /></div>
        <h2>{title}</h2>
        <p>{text}</p>
        {chips.length ? (
          <div className="landing-story__chips" aria-label="Funciones incluidas">
            {chips.map((chip) => <span key={chip}>{chip}</span>)}
          </div>
        ) : null}
      </div>
      <div className="landing-story__section-visual">{children}</div>
    </section>
  );
}

function ProductWindow({ src, alt, label, compact = false, eager = false }) {
  return (
    <figure className={`landing-story__window${compact ? " is-compact" : ""}`}>
      <figcaption>
        <span className="landing-story__window-dots" aria-hidden="true"><i /><i /><i /></span>
        {label}
      </figcaption>
      <img
        src={src}
        alt={alt}
        loading={eager ? "eager" : "lazy"}
        fetchPriority={eager ? "high" : "auto"}
        decoding="async"
      />
    </figure>
  );
}

function SteamPreview() {
  return (
    <div className="landing-story__steam-preview" aria-label="Ejemplo de una biblioteca importada desde Steam">
      <div className="landing-story__steam-source">
        <SteamIcon />
        <div><span>Tu cuenta</span><strong>Biblioteca de Steam</strong><small>Juegos y horas verificadas</small></div>
      </div>
      <ArrowRight className="landing-story__steam-arrow" size={30} aria-hidden="true" />
      <div className="landing-story__steam-result">
        <span>Tu historia</span><strong>Lista para continuar</strong>
        <ul>
          <li><CheckCircle2 size={16} /> Juegos importados</li>
          <li><CheckCircle2 size={16} /> Horas verificadas</li>
          <li><CheckCircle2 size={16} /> Perfil con contenido</li>
        </ul>
      </div>
    </div>
  );
}

function SessionPreview() {
  return (
    <article className="landing-story__session" aria-label="Ejemplo de una sesión de juego publicada">
      <div className="landing-story__session-art" aria-hidden="true">
        <span>Sesión de juego</span><strong>ELDEN<br />RING</strong>
      </div>
      <div className="landing-story__session-body">
        <span className="landing-story__session-source">PC · Sesión publicada</span>
        <h3>2 h 34 min</h3>
        <blockquote>“Por fin he llegado a Leyndell.”</blockquote>
        <div className="landing-story__session-meta">
          <span>+182 XP</span>
          <span><MessageCircle size={15} /> Comentarios</span>
          <span>♡ Reacciones</span>
        </div>
      </div>
    </article>
  );
}

function ProgressPreview() {
  return (
    <div className="landing-story__progress" aria-label="Ejemplo de progreso gamer en XPHub">
      <div className="landing-story__level"><span>Nivel</span><strong>78</strong></div>
      <div className="landing-story__progress-copy">
        <span>Tu progreso gamer</span>
        <strong>8.420 / 9.105 XP</strong>
        <div className="landing-story__progress-track"><i /></div>
        <ul>
          <li><BarChart3 size={17} /> Tus horas reunidas</li>
          <li><ListChecks size={17} /> Tu biblioteca creciendo</li>
          <li><Trophy size={17} /> Nuevos hitos por descubrir</li>
        </ul>
      </div>
    </div>
  );
}

function SteamIcon() {
  return (
    <svg className="landing-story__steam-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M11.98 0C5.68 0 .51 4.86.02 11.04l6.43 2.66c.55-.37 1.2-.59 1.92-.59h.18l2.86-4.14C11.42 6.49 13.44 4.47 15.92 4.47a4.5 4.5 0 1 1-.1 9l-4.08 2.92v.15a3.38 3.38 0 0 1-6.7.67L.44 15.27A12 12 0 1 0 11.98 0Zm-4.44 18.21-1.47-.61a2.55 2.55 0 1 0 1.4-1.49l1.52.63a1.88 1.88 0 1 1-1.45 3.47Zm8.4-6.29a3.02 3.02 0 1 1 0-6.03 3.02 3.02 0 0 1 0 6.03Zm0-.75a2.27 2.27 0 1 1 0-4.53 2.27 2.27 0 0 1 0 4.53Z" />
    </svg>
  );
}
